import React from "react";

// Chart-room compass rose, drawn on canvas — the ambient brand motif for the
// Home hero. Reveals once on mount (concentric depth rings + bezel ticks + a
// north needle), then holds. Honors prefers-reduced-motion by drawing the final
// frame immediately. Colors default to the on-navy palette but are overridable.
export default function CompassRose({
  accent = "#d9a560",
  line = "rgba(255,255,255,0.14)",
  muted = "rgba(255,255,255,0.34)",
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let raf;
    let start = null;
    let dim = 0;
    let cx = 0;
    let cy = 0;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      dim = rect.width || 280;
      canvas.width = dim * DPR;
      canvas.height = dim * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cx = dim / 2;
      cy = dim / 2;
    }

    function draw(p) {
      ctx.clearRect(0, 0, dim, dim);
      const R = dim * 0.46;

      const rings = 4;
      for (let i = 1; i <= rings; i++) {
        const rr = R * (i / rings) * p;
        ctx.beginPath();
        ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.strokeStyle = i === rings ? accent : line;
        ctx.globalAlpha = (i === rings ? 0.9 : 0.6) * p;
        ctx.lineWidth = i === rings ? 1.4 : 1;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      const ticks = 72;
      for (let t = 0; t < ticks; t++) {
        const ang = (t / ticks) * Math.PI * 2 - Math.PI / 2;
        const major = t % 6 === 0;
        const outer = R;
        const inner = R - (major ? dim * 0.045 : dim * 0.022);
        const reveal = Math.max(0, Math.min(1, (p * ticks - t) / 6));
        if (reveal <= 0) continue;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(ang) * outer, cy + Math.sin(ang) * outer);
        ctx.lineTo(cx + Math.cos(ang) * inner, cy + Math.sin(ang) * inner);
        ctx.strokeStyle = major ? accent : muted;
        ctx.globalAlpha = (major ? 0.85 : 0.4) * reveal;
        ctx.lineWidth = major ? 1.5 : 1;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      ctx.strokeStyle = line;
      ctx.globalAlpha = 0.5 * p;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx, cy - R);
      ctx.lineTo(cx, cy + R);
      ctx.moveTo(cx - R, cy);
      ctx.lineTo(cx + R, cy);
      ctx.stroke();

      const nlen = R * 0.62 * p;
      ctx.beginPath();
      ctx.moveTo(cx, cy - nlen);
      ctx.lineTo(cx - dim * 0.028, cy);
      ctx.lineTo(cx + dim * 0.028, cy);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.globalAlpha = 0.92 * p;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    function frame(ts) {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / 1400);
      draw(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(frame);
    }

    resize();
    if (reduce) draw(1);
    else raf = requestAnimationFrame(frame);

    const onResize = () => {
      resize();
      draw(1);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [accent, line, muted]);

  return (
    <canvas
      ref={ref}
      style={{ width: "100%", height: "100%", display: "block" }}
      aria-hidden="true"
    />
  );
}
